// src/pages/Dashboard.js
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { isFullAccess } from '../permissions';
import { getDashboard, getMyPendingTransfers, getMyToolRequests, updateToolRequest } from '../services/api';

export default function Dashboard() {
  const { worker } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(false);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [toolRequests, setToolRequests]         = useState([]);
  const [decliningId, setDecliningId]           = useState(null);
  const role = worker?.role || 'view_only';

  const isFieldWorker = ['carpenter', 'laborer', 'view_only'].includes(role);
  const isForeman     = ['foreman', 'site_supervisor', 'general_superintendent'].includes(role);
  const isPM          = ['project_manager', 'project_engineer', 'project_administrator'].includes(role);
  const isAdmin       = isFullAccess(role);

  useEffect(() => {
    if (!isFieldWorker) {
      getDashboard()
        .then(r => setStats(r.data))
        .catch(() => setStatsError(true));
    }
    // Check for pending transfers and tool requests for everyone
    getMyPendingTransfers()
      .then(r => setPendingTransfers(r.data.transfers || []))
      .catch(() => {});
    getMyToolRequests()
      .then(r => setToolRequests(r.data.requests || []))
      .catch(() => {});
  }, [isFieldWorker]);

  const handleDeclineRequest = async (requestId) => {
    setDecliningId(requestId);
    try {
      await updateToolRequest(requestId, { status: 'cancelled' });
      setToolRequests(prev => prev.filter(r => r.request_id !== requestId));
    } catch (err) {
      console.error('Failed to decline request:', err);
    } finally {
      setDecliningId(null);
    }
  };

  const incoming = pendingTransfers.filter(t => t.direction === 'incoming');
  const outgoing = pendingTransfers.filter(t => t.direction === 'outgoing');
  const requestsOnMyTools = toolRequests.filter(r => r.direction === 'incoming');
  const myOutgoingRequests = toolRequests.filter(r => r.direction === 'outgoing');

  const hasBanners = pendingTransfers.length > 0 || toolRequests.length > 0;

  const PendingBanner = () => {
    if (!hasBanners) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        {/* Someone wants a tool I hold */}
        {requestsOnMyTools.length > 0 && (
          <div style={{
            background: '#2d1f00', border: '1px solid #d97706',
            borderRadius: 10, padding: '12px 16px', marginBottom: 8,
          }}>
            <p style={{ color: '#fbbf24', fontWeight: 700, marginBottom: 6 }}>
              📋 {requestsOnMyTools.length} tool request{requestsOnMyTools.length > 1 ? 's' : ''} on your equipment
            </p>
            {requestsOnMyTools.map(r => (
              <div key={r.request_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 8 }}>
                <p style={{ color: '#fde68a', fontSize: '0.88rem', margin: 0, flex: 1 }}>
                  {r.requested_by_name} needs: {r.item_name}{r.asset_tag ? ` (${r.asset_tag})` : ''}
                  {r.requested_for_location_name ? ` — for ${r.requested_for_location_name}` : ''}
                </p>
                <button
                  onClick={() => handleDeclineRequest(r.request_id)}
                  disabled={decliningId === r.request_id}
                  style={{
                    background: '#7f1d1d', color: '#fca5a5', border: '1px solid #dc2626',
                    borderRadius: 6, padding: '3px 10px', fontSize: '0.75rem',
                    fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                    opacity: decliningId === r.request_id ? 0.5 : 1
                  }}
                >
                  {decliningId === r.request_id ? '…' : 'Decline'}
                </button>
              </div>
            ))}
            <p style={{ color: '#d97706', fontSize: '0.78rem', marginTop: 6 }}>
              Coordinate with your foreman or return to warehouse.
            </p>
          </div>
        )}

        {/* My outgoing tool requests */}
        {myOutgoingRequests.length > 0 && (
          <div style={{
            background: '#1c1f2e', border: '1px solid #6366f1',
            borderRadius: 10, padding: '12px 16px', marginBottom: 8,
          }}>
            <p style={{ color: '#a5b4fc', fontWeight: 700, marginBottom: 6 }}>
              📋 {myOutgoingRequests.length} pending tool request{myOutgoingRequests.length > 1 ? 's' : ''} you submitted
            </p>
            {myOutgoingRequests.map(r => (
              <p key={r.request_id} style={{ color: '#c7d2fe', fontSize: '0.88rem', marginBottom: 2 }}>
                {r.item_name}{r.asset_tag ? ` (${r.asset_tag})` : ''}
                {r.current_holder_name ? ` — with ${r.current_holder_name}` : ''}
                {r.requested_for_location_name ? ` for ${r.requested_for_location_name}` : ''}
              </p>
            ))}
          </div>
        )}

        {/* Incoming transfers */}
        {incoming.length > 0 && (
          <div style={{
            background: '#1a2e1a', border: '1px solid #22c55e',
            borderRadius: 10, padding: '12px 16px', marginBottom: 8,
          }}>
            <p style={{ color: '#22c55e', fontWeight: 700, marginBottom: 6 }}>
              📥 {incoming.length} incoming transfer{incoming.length > 1 ? 's' : ''} waiting for you
            </p>
            {incoming.map(t => (
              <div key={t.transfer_id} style={{ marginBottom: 4 }}>
                <span style={{ color: '#ccc', fontSize: '0.9rem' }}>
                  {t.item_name}{t.asset_tag ? ` (${t.asset_tag})` : ''} from {t.from_name}
                </span>
                {' '}
                <a href={t.accept_url}
                  style={{ color: '#22c55e', fontSize: '0.85rem', textDecoration: 'underline' }}>
                  Accept / Reject
                </a>
              </div>
            ))}
          </div>
        )}

        {/* Outgoing transfers */}
        {outgoing.length > 0 && (
          <div style={{
            background: '#1a1f2e', border: '1px solid #3b82f6',
            borderRadius: 10, padding: '12px 16px',
          }}>
            <p style={{ color: '#60a5fa', fontWeight: 700, marginBottom: 6 }}>
              📤 {outgoing.length} outgoing transfer{outgoing.length > 1 ? 's' : ''} awaiting acceptance
            </p>
            {outgoing.map(t => (
              <p key={t.transfer_id} style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: 2 }}>
                {t.item_name}{t.asset_tag ? ` (${t.asset_tag})` : ''} → {t.to_name}
              </p>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Simple field worker screen
  if (isFieldWorker) {
    return (
      <div className="screen">
        <div className="screen-header" style={{ justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ color: 'white' }}>ASSET TRACKER: Next Generation Const</h2>
            <p style={{ color: 'rgba(255,255,255,1)', fontSize: '0.8rem' }}>
              {worker?.display_name || worker?.first_name}
            </p>
          </div>
          <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,1)', textAlign: 'right' }}>
            {worker?.job_title || 'Field Worker'}
          </div>
        </div>

        <div className="screen-content" style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 16 }}>
          <PendingBanner />

          <button
            className="btn btn-primary"
            style={{ minHeight: 80, fontSize: '1.2rem', borderRadius: 16 }}
            onClick={() => navigate('/scan')}
          >
            📷 Scan a Tool
          </button>

          <button
            className="btn btn-secondary"
            style={{ minHeight: 64, fontSize: '1.1rem', borderRadius: 16 }}
            onClick={() => navigate('/my-items')}
          >
            📦 My Items
          </button>

          <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 8 }}>
            Scan a tool's QR code to check in, check out, or transfer it
          </p>
        </div>
      </div>
    );
  }

  // Full dashboard for foreman/super/PM/admin
  return (
    <div className="screen">
      <div className="screen-header" style={{ justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ color: 'white' }}>ASSET TRACKER: Next Generation Const</h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
            {worker?.display_name || worker?.first_name}
          </p>
        </div>
        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', textAlign: 'right' }}>
          {worker?.job_title || role}
        </div>
      </div>

      <div className="screen-content">
        <PendingBanner />
        {statsError && (
          <div className="alert alert-error" style={{ marginBottom: 12 }}>
            Could not load dashboard stats. Check your connection.
          </div>
        )}
        {/* Stats row */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
            {[
              { label: 'Total Assets', value: stats.total_assets,    icon: '🔧', color: null },
              { label: 'Checked Out',  value: stats.checked_out,     icon: '📤', color: 'var(--procore-orange)' },
              { label: 'Overdue',      value: stats.overdue_items,   icon: '⚠️', color: 'var(--status-lost)' },
              { label: 'Low Stock',    value: stats.low_stock_items, icon: '📦', color: 'var(--status-in-repair)' },
            ].map(s => (
              <div key={s.label} className="card" style={{ textAlign: 'center', padding: 12 }}>
                <div style={{ fontSize: '1.3rem' }}>{s.icon}</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 700, color: s.color || 'var(--text-primary)' }}>
                  {s.value ?? '—'}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Quick actions */}
        <h3 style={{ marginBottom: 12 }}>Quick Actions</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          <button className="btn btn-primary" style={{ minHeight: 56 }} onClick={() => navigate('/scan')}>
            📷 Scan a Tool
          </button>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => navigate('/my-items')}>
              📦 My Items
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/search')}>
              🔍 Search
            </button>
          </div>
          {(isPM || isAdmin) && (
            <button className="btn btn-navy" onClick={() => navigate('/admin')}>
              ⚙️ Admin Dashboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
}